import { useEffect, useMemo, useRef, useState } from "react";

import { Box, Grid, Table } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { Button, EmptyMessage, Loader, Pagination, TextInput } from "components";
import { IRootState } from "config/redux";
import { AuthorityConstants, RouteConstants, ServiceConstants } from "constant";
import { useListHook } from "hooks";
import { UserService } from "services";
import { tableClasses } from "styles";
import { ClientListResponse, CreditStatus } from "types";
import { AppUtils, UserUtils } from "utils";

import { ClientStatusFilter } from "./ClientStatusFilterBar";
import ClientListItem from "./item";

const { ROLE_CR_R, ROLE_USR_AGT_REASSIGN } = AuthorityConstants;
const { REQUEST_PENDING, REQUEST_USERS_CLIENTS_ID } = ServiceConstants;
interface ClientsListProps {
  fetch: (page: number, search: string | undefined, source: string) => void;
  clientStatus: ClientStatusFilter;
  creditRequestStatus?: CreditStatus;
  addNew?: () => void;
  clientRoute?: string;
}

let searchTimeout: NodeJS.Timeout | undefined = undefined;

const ClientsList = ({
  addNew,
  fetch,
  clientStatus,
  creditRequestStatus,
  clientRoute = RouteConstants.ROUTE_CLIENTS,
}: ClientsListProps) => {
  const { t } = useTranslation();
  const serviceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_USERS_CLIENTS_ID],
  );
  const { page, content, updatePageable, resetList, totalPages, refreshList, setPage } =
    useListHook();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [searchParams] = useSearchParams();
  const sourceFilter = searchParams.get("source") ?? "";
  const searchInitialized = useRef(false);

  useEffect(() => {
    resetList();
  }, [clientStatus, creditRequestStatus, resetList]);

  useEffect(() => {
    if (clientStatus !== "UNASSIGNED" || !UserUtils.hasAuthority(ROLE_USR_AGT_REASSIGN)) {
      return;
    }
    UserService.usersAgentsAll({ page: 0, search: "" });
  }, [clientStatus]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_USERS_CLIENTS_ID, (val) => {
      updatePageable(val);
    });
  }, [serviceObject, updatePageable]);

  useEffect(() => {
    if (page === undefined) {
      return;
    }
    if (page === -1) {
      resetList();
      return;
    }
    fetch(page, debouncedSearch, sourceFilter);
  }, [debouncedSearch, fetch, page, resetList, sourceFilter]);

  useEffect(() => {
    if (!searchInitialized.current) {
      searchInitialized.current = true;
      return;
    }
    if (debouncedSearch === undefined) {
      return;
    }
    const length = debouncedSearch.trim().length;
    if (length > 0 && length < 3) {
      return;
    }
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(refreshList, 500);
  }, [debouncedSearch, refreshList]);

  const isLoading = useMemo(
    () => serviceObject && serviceObject.status === REQUEST_PENDING,
    [serviceObject],
  );

  const rows = useMemo(() => {
    if (!content) {
      return <></>;
    }
    return content.map((el: ClientListResponse) => (
      <ClientListItem
        key={el.user.id}
        item={el}
        clientRoute={clientRoute}
        clientStatus={clientStatus}
        onAssigned={refreshList}
        t={t}
      />
    ));
  }, [clientRoute, clientStatus, content, refreshList, t]);

  return (
    <Box key={`list-${clientStatus}-${creditRequestStatus ?? "none"}`}>
      <Grid justify={"flex-end"}>
        {addNew && (
          <Grid.Col span={{ sm: 1, md: 4, lg: 8 }}>
            <Button onClick={addNew} size={"sm"}>
              <IconPlus />
            </Button>
          </Grid.Col>
        )}
        <Grid.Col span={{ sm: addNew ? 11 : 12, md: 8, lg: 4 }}>
          <TextInput
            placeholder={t("search.clients")}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </Grid.Col>
      </Grid>
      <Box pos={"relative"}>
        <Loader overlay={true} visible={content === undefined || isLoading} />
        {content &&
          (content.length === 0 ? (
            <EmptyMessage message={t("clients.list.empty")} />
          ) : (
            <>
              <Table className={tableClasses.table} stickyHeader stickyHeaderOffset={56}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("user.fullName.label")}</Table.Th>
                    <Table.Th visibleFrom={"md"}>{t("user.gender.label")}</Table.Th>
                    <Table.Th>{t("user.phone.label")}</Table.Th>
                    <Table.Th visibleFrom={"md"}>{t("credit.partnerLastName.short")}</Table.Th>
                    <Table.Th visibleFrom={"md"}>{t("credit.partnerPhoneNumber.short")}</Table.Th>
                    <Table.Th visibleFrom={"md"}>{t("user.address.city.label")}</Table.Th>
                    <Table.Th>{t("source.title")}</Table.Th>
                    <Table.Th>{t("user.acquisition.label")}</Table.Th>
                    <Table.Th visibleFrom={"md"} colSpan={2}>
                      {t("clients.date.change.label")}
                    </Table.Th>
                    <Table.Th>{t("credit.status.label")}</Table.Th>
                    <Table.Th ta={"center"}>{t("credit.comment.title")}</Table.Th>
                    {UserUtils.hasAuthority(ROLE_CR_R) && (
                      <Table.Th ta={"center"}>{t("link.title")}</Table.Th>
                    )}
                    <Table.Th>{t("credit.agent.label")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </>
          ))}
      </Box>
    </Box>
  );
};

export default ClientsList;
